<script setup lang="ts">
    import { ref, computed } from 'vue'

    import type { SurveyCriteriaManagerDialogViewModel } from '@Evaluation/AlternativesSurveys/Components/SurveyCriteriaManagerDialog'

    /* ---------------- props / emits ---------------- */

    const props = defineProps<{
        viewModel: SurveyCriteriaManagerDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'update-survey-criteria', surveyId: number, selectedCriteriaIds: number[]): void
    }>()

    /* ---------------- shortcuts ---------------- */

    const vm = computed(() => props.viewModel)
    const opened = computed(() => vm.value?.opened ?? false)

    /* ---------------- derived ---------------- */

    const initialized = computed(() => vm.value.rm != null)
    const criteria = computed(() => vm.value.rm?.Criteria ?? [])
    const surveyId = computed(() => vm.value.surveyId)
    const surveyName = computed(() => vm.value.surveyName)

    /* ---------------- local state ---------------- */

    const searchPhrase = ref<string | null>(null)

    /* ---------------- computed ---------------- */

    const criteriaView = computed(() =>
    {
        if (searchPhrase.value)
        {
            const phrase = searchPhrase.value.toLowerCase()
            return criteria.value.filter(c =>
                (c.Path + c.Name).toLowerCase().includes(phrase)
            )
        }

        return criteria.value
    })

    /* ---------------- actions ---------------- */

    function emitUpdateCriteria()
    {
        const selectedCriteriaIds = criteria.value
            .filter(c => c.IsIncluded === true)
            .map(c => c.Id)

        emit('update-survey-criteria', surveyId.value, selectedCriteriaIds)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content" style="height: calc(100vh - 50px);">
                    <div class="modal-header">
                        <h5 class="modal-title">Select criteria: {{surveyName}}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="initialized === false"><i class="fa fa-spinner fa-spin"></i> Loading...</h2>
                        <div v-if="initialized">
                            <div class="alert alert-info alert-dismissible" role="alert">
                                <button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                You can limit which criteria are included in your panel. To do that, simply check the criteria you want to include from the list below.
                                <strong>WARNING: New criteria won't be included in panels automatically.</strong>
                            </div>

                            <div class="input-group input-group-lg">
                                <input type="search" placeholder="Search for a criterion..." class="form-control" v-model="searchPhrase">
                                <span class="input-group-append">
                                    <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                                </span>
                            </div>

                            <br />
                            <div class="list-group">
                                <button type="button" v-for="c in criteriaView" :key="c.Id" @click="c.IsIncluded = !c.IsIncluded" class="list-group-item list-group-item-action">
                                    <span v-if="c.IsIncluded === true" class="fa-regular fa-square-check"></span>
                                    <span v-if="c.IsIncluded === false" class="fa-regular fa-square"></span>
                                    {{c.Path}}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="emitUpdateCriteria" type="button" class="btn btn-primary">Save &amp; Close</button>
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>